import styled from 'styled-components';
import { setAlpha } from '../../../../styles';
import { Box } from '../../../Core';
import { NumberWrapper } from '../../../FormattedNumber';
import { Text } from '../../../Text';

const PRICE_COLUMN_WIDTH = '80px';
const SPREAD_COLUMN_WIDTH = '44px';
const PRICE_ROW_COLUMN_GAP_COUNT = 2;

const securityMarketDataPriceRowWidth = ({ theme }: { theme: { spacing4: number } }) =>
  `calc(${PRICE_COLUMN_WIDTH} + ${SPREAD_COLUMN_WIDTH} + ${PRICE_COLUMN_WIDTH} + ${PRICE_ROW_COLUMN_GAP_COUNT * theme.spacing4}px)`;

export const PriceClickTarget = styled(Box)<{ clickable?: boolean; $highlightVariant?: 'bid' | 'offer' }>`
  background-color: ${({ $highlightVariant, theme }) => {
    if ($highlightVariant === 'bid') {
      return setAlpha(0.4, theme.colors.green.default);
    }
    if ($highlightVariant === 'offer') {
      return setAlpha(0.4, theme.colors.red.default);
    }
    return 'transparent';
  }};
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  box-sizing: border-box;
  cursor: ${({ clickable }) => (clickable ? 'pointer' : 'default')};
  padding: ${({ theme }) => `${theme.spacing2}px ${theme.spacing8}px`};
`;

export const SecurityMarketDataCellGrid = styled(Box)`
  display: grid;
  grid-template-columns: minmax(${securityMarketDataPriceRowWidth}, 1fr) auto;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing8}px;
  width: 100%;
  min-width: 0;
`;

export const SecurityMarketDataPricesSlot = styled(Box)`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing8}px;
  min-width: ${securityMarketDataPriceRowWidth};
  width: 100%;
`;

export const SecurityMarketDataPriceRowGrid = styled(Box)`
  display: grid;
  grid-template-columns: 1fr ${SPREAD_COLUMN_WIDTH} 1fr;
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing4}px;
  width: 100%;
`;

const SecurityMarketDataPriceColumnBase = styled(PriceClickTarget)`
  width: 100%;
  min-width: 0;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  &&&& ${NumberWrapper}, &&&&:hover ${NumberWrapper} {
    background-color: transparent;
  }
`;

export const SecurityMarketDataBidPriceColumn = styled(SecurityMarketDataPriceColumnBase)`
  display: flex;
  align-items: center;
`;

export const SecurityMarketDataOfferPriceColumn = styled(SecurityMarketDataPriceColumnBase)`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  text-align: right;
`;

export const PriceWithUnit = styled.span<{ $align: 'left' | 'right' }>`
  align-items: center;
  column-gap: ${({ theme }) => theme.spacing4}px;
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  width: 100%;

  & > :first-child,
  ${NumberWrapper} {
    justify-self: ${({ $align }) => ($align === 'right' ? 'end' : 'start')};
    min-width: 0;
  }
`;

export const PriceUnit = styled(Text)`
  color: ${({ theme }) => theme.colorTextSubtle};
  flex-shrink: 0;
`;

export const SecurityMarketDataActionsSlot = styled(Box)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing4}px;
  flex-shrink: 0;
  justify-content: flex-end;
  min-width: 120px;
`;

export const SpreadSlot = styled(Box)`
  width: ${SPREAD_COLUMN_WIDTH};
  flex-shrink: 0;
`;

export const SpreadPill = styled(SpreadSlot)`
  background-color: ${({ theme }) => theme.backgroundContentHover};
  border-radius: ${({ theme }) => theme.borderRadiusDefault}px;
  color: ${({ theme }) => theme.colorTextVolumeLadderSpread};
  font-size: ${({ theme }) => theme.fontSizeTiny}rem;
  line-height: ${({ theme }) => theme.lineHeightTiny}rem;
  padding: ${({ theme }) => `${theme.spacing2}px ${theme.spacing4}px`};
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
